import { QuickstartGuideName } from "@ui/pages/program-builder/quickstart/quickstart-analytics";
import { userManager } from "@core/service-container";

const STORAGE_KEY = "programBuilder.quickstart.completedGuides";

type CompletedGuides = Partial<Record<QuickstartGuideName, boolean>>;
type StorageShape = Partial<Record<string, CompletedGuides>>;

const GUIDE_KEYS: QuickstartGuideName[] = [
  "ADD_FIRST_EXERCISE",
  "FILTER_LIBRARY",
  "ADD_COMPLEMENTARY_EXERCISES",
  "ADD_ALTERNATIVE_EXERCISES",
  "DUPLICATE_SESSION",
  "START_SESSION",
];

function currentUserKey(): string {
  const user = userManager.user.get();
  return user?.id ?? "__anonymous";
}

function isLegacyCompletedGuides(obj: unknown): obj is CompletedGuides {
  if (!obj || typeof obj !== "object") return false;
  const keys = Object.keys(obj);
  return keys.some(k => (GUIDE_KEYS as string[]).includes(k));
}

function readAll(): StorageShape {
  if (!("localStorage" in globalThis)) return {};
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};

    if (isLegacyCompletedGuides(parsed)) {
      return { __legacy: parsed };
    }

    return parsed as StorageShape;
  } catch (error_) {
    console.debug("[QuickstartCompletionStorage] readAll failed", error_);
    return {};
  }
}

function readForCurrentUser(): CompletedGuides {
  const all = readAll();
  const key = currentUserKey();
  if (key === "__anonymous" && all.__legacy) return all.__legacy;
  return all[key] ?? {};
}

function writeForCurrentUser(state: CompletedGuides): void {
  if (!("localStorage" in globalThis)) return;
  try {
    const all = readAll();
    const key = currentUserKey();
    all[key] = state;
    globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (error_) {
    console.debug("[QuickstartCompletionStorage] write failed", error_);
    return;
  }
}

export function hasGuideCompletionTracked(guide: QuickstartGuideName): boolean {
  const state = readForCurrentUser();
  return state[guide] === true;
}

export function markGuideCompletionTracked(guide: QuickstartGuideName): void {
  const state = readForCurrentUser();
  if (state[guide]) return;
  state[guide] = true;
  writeForCurrentUser(state);
}
